/**
 * Activity / Audit-Log timeline (Story 34.5 — Epic 34).
 *
 * A reverse-chronological table of every audited change in the workspace, styled
 * to match the clients list (breadcrumb → filter toolbar → table). Reads the live
 * GET /api/audit-events cursor API via `useAuditEvents` (infinite query). Filters
 * (actor / resource type / action / date range) live in the URL search params;
 * changing any filter starts a fresh paginated query (the query key embeds the
 * filters, so pagination resets automatically).
 *
 * Access: gated on VIEW_AUDIT_LOG (Owner + Admin). Viewers/Members get an
 * access-denied state. The server enforces the same gate; this is the UI mirror.
 */

import { useEffect, useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  ListMagnifyingGlass,
  LockKey,
  WarningCircle,
  ArrowClockwise,
} from "@phosphor-icons/react";
import { ReportBreadcrumb } from "../../components/reports/ReportBreadcrumb";
import { usePermission, Actions } from "../../hooks/usePermission";
import { useDebounce } from "../../hooks/useDebounce";
import { useAuditEvents, flattenAuditEvents } from "../../hooks/useAuditEvents";
import type { AuditEventDto, AuditEventFilters } from "../../hooks/useAuditEvents";
import {
  ActivityFilterToolbar,
  type DateRangePreset,
} from "../../components/activity/ActivityFilterToolbar";
import { ActivityTableView } from "../../components/activity/ActivityTableView";
import { EventDetailDialog } from "../../components/activity/EventDetailDialog";
import { Button } from "@provenleap/ui";

// ---------------------------------------------------------------------------
// URL search params
// ---------------------------------------------------------------------------

export interface ActivitySearch {
  actor?: string;
  /** Comma-joined resource-type values. */
  resources?: string;
  /** Comma-joined action values. */
  actions?: string;
  /** "24h" | "7d" | "30d" | "custom". */
  range?: string;
  /** ISO yyyy-mm-dd custom range bounds. */
  from?: string;
  to?: string;
}

const VALID_RANGES = new Set(["24h", "7d", "30d", "custom"]);

export const Route = createFileRoute("/_authenticated/activity")({
  validateSearch: (search: Record<string, unknown>): ActivitySearch => ({
    actor: typeof search.actor === "string" ? search.actor : undefined,
    resources: typeof search.resources === "string" ? search.resources : undefined,
    actions: typeof search.actions === "string" ? search.actions : undefined,
    range:
      typeof search.range === "string" && VALID_RANGES.has(search.range)
        ? search.range
        : undefined,
    from: typeof search.from === "string" ? search.from : undefined,
    to: typeof search.to === "string" ? search.to : undefined,
  }),
  component: ActivityPage,
});

// ---------------------------------------------------------------------------
// Date-preset → epoch-ms derivation
// ---------------------------------------------------------------------------

const DAY_MS = 24 * 60 * 60 * 1000;

/** Translate the date filter into the API's startTime/endTime (ms). */
function deriveTimeWindow(
  datePreset: DateRangePreset,
  customStart: string | undefined,
  customEnd: string | undefined,
  now: number
): { startTime?: number; endTime?: number } {
  switch (datePreset) {
    case "24h":
      return { startTime: now - DAY_MS };
    case "7d":
      return { startTime: now - 7 * DAY_MS };
    case "30d":
      return { startTime: now - 30 * DAY_MS };
    case "custom": {
      const start = customStart ? new Date(`${customStart}T00:00:00`).getTime() : undefined;
      // Custom end is inclusive of the whole day.
      const end = customEnd ? new Date(`${customEnd}T23:59:59.999`).getTime() : undefined;
      return {
        startTime: Number.isNaN(start as number) ? undefined : start,
        endTime: Number.isNaN(end as number) ? undefined : end,
      };
    }
    default:
      return {};
  }
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function ActivityPage() {
  const { hasPermission } = usePermission();
  const canView = hasPermission(Actions.VIEW_AUDIT_LOG);

  return (
    <div className="p-6 max-w-[1800px] mx-auto">
      <ReportBreadcrumb items={[{ label: "Activity" }]} />
      <div className="mb-6" />

      {canView ? <ActivityFeed /> : <AccessDenied />}
    </div>
  );
}

function AccessDenied() {
  return (
    <div
      data-testid="activity-access-denied"
      className="rounded-[22px] bg-background p-12 shadow-xs text-center"
      style={{ border: "0.5px solid var(--hairline)" }}
    >
      <div className="mx-auto flex items-center justify-center w-14 h-14 rounded-2xl bg-muted mb-4">
        <LockKey size={28} className="text-muted-foreground" />
      </div>
      <h2 className="text-[15px] font-semibold text-foreground">
        You don't have access to the activity log
      </h2>
      <p className="text-[13px] text-muted-foreground mt-2 max-w-md mx-auto">
        Only workspace owners and admins can view the audit timeline. Ask an
        administrator if you need access.
      </p>
    </div>
  );
}

function ActivityFeed() {
  const navigate = useNavigate();
  const { actor, resources, actions, range, from, to } = Route.useSearch();

  // Pin a render-time "now" so preset windows are stable within a render pass.
  const now = useMemo(() => Date.now(), []);

  // ── Search state (clients pattern: local input + debounce → API filter) ──
  const [searchInput, setSearchInput] = useState(actor ?? "");
  const debouncedSearch = useDebounce(searchInput, 300);
  useEffect(() => {
    setSearchInput(actor ?? "");
  }, [actor]);

  // ── URL → derived filter values ────────────────────────────────────
  const selectedResourceTypes = useMemo(
    () => (resources ? resources.split(",").filter(Boolean) : []),
    [resources]
  );
  const selectedActions = useMemo(
    () => (actions ? actions.split(",").filter(Boolean) : []),
    [actions]
  );
  const datePreset = (range as DateRangePreset) ?? null;

  // ── derived → API filters (the query key) ──────────────────────────
  const apiFilters: AuditEventFilters = useMemo(() => {
    const { startTime, endTime } = deriveTimeWindow(datePreset, from, to, now);
    return {
      actorId: debouncedSearch || undefined,
      resourceType: selectedResourceTypes.length > 0 ? selectedResourceTypes : undefined,
      action: selectedActions.length > 0 ? selectedActions : undefined,
      startTime,
      endTime,
    };
  }, [debouncedSearch, selectedResourceTypes, selectedActions, datePreset, from, to, now]);

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useAuditEvents(apiFilters);

  const events = useMemo(() => flattenAuditEvents(data?.pages), [data]);

  // ── selected event for the detail modal (kept after close for exit anim) ──
  const [selected, setSelected] = useState<AuditEventDto | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const handleSelect = (event: AuditEventDto) => {
    setSelected(event);
    setDetailOpen(true);
  };

  // ── filter mutators (write to URL; query key change resets pagination) ──
  const setSearch = (patch: Partial<ActivitySearch>) => {
    navigate({
      from: Route.fullPath,
      search: (prev) => ({ ...prev, ...patch }),
      replace: true,
    });
  };

  const onSearchChange = (value: string) => {
    setSearchInput(value);
    setSearch({ actor: value || undefined });
  };

  const onToggleResourceType = (rt: string) => {
    const next = selectedResourceTypes.includes(rt)
      ? selectedResourceTypes.filter((x) => x !== rt)
      : [...selectedResourceTypes, rt];
    setSearch({ resources: next.length > 0 ? next.join(",") : undefined });
  };

  const onToggleAction = (action: string) => {
    const next = selectedActions.includes(action)
      ? selectedActions.filter((x) => x !== action)
      : [...selectedActions, action];
    setSearch({ actions: next.length > 0 ? next.join(",") : undefined });
  };

  const onDatePresetChange = (preset: DateRangePreset) => {
    setSearch({
      range: preset ?? undefined,
      // Drop stale custom bounds when leaving custom mode.
      ...(preset === "custom" ? {} : { from: undefined, to: undefined }),
    });
  };

  const onCustomRangeChange = (start: string | undefined, end: string | undefined) =>
    setSearch({ from: start, to: end });

  const onClearFilters = () => {
    setSearchInput("");
    setSearch({
      actor: undefined,
      resources: undefined,
      actions: undefined,
      range: undefined,
      from: undefined,
      to: undefined,
    });
  };

  return (
    <div>
      <ActivityFilterToolbar
        search={searchInput}
        onSearchChange={onSearchChange}
        selectedResourceTypes={selectedResourceTypes}
        onToggleResourceType={onToggleResourceType}
        selectedActions={selectedActions}
        onToggleAction={onToggleAction}
        datePreset={datePreset}
        onDatePresetChange={onDatePresetChange}
        customStart={from}
        customEnd={to}
        onCustomRangeChange={onCustomRangeChange}
        onClearFilters={onClearFilters}
      />

      {isLoading ? (
        <ActivitySkeleton />
      ) : isError ? (
        <ErrorState
          message={error instanceof Error ? error.message : "Something went wrong."}
          onRetry={() => refetch()}
        />
      ) : events.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          <ActivityTableView events={events} onSelect={handleSelect} />

          {hasNextPage && (
            <div className="mt-4 flex justify-center">
              <Button
                variant="outline"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="rounded-2xl text-[13px] shadow-xs cursor-pointer hover:bg-background hover:-translate-y-0.5 hover:shadow-sm transition-all"
                style={{ border: "0.5px solid var(--hairline)" }}
              >
                {isFetchingNextPage ? "Loading…" : "Load more"}
              </Button>
            </div>
          )}
        </>
      )}

      <EventDetailDialog
        event={selected}
        open={detailOpen}
        onOpenChange={(open) => {
          setDetailOpen(open);
          if (!open) {
            // Keep `selected` briefly so the dialog's exit animation has content.
            setTimeout(() => setSelected(null), 300);
          }
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// States
// ---------------------------------------------------------------------------

function ActivitySkeleton() {
  return (
    <div
      data-testid="activity-skeleton"
      aria-hidden="true"
      className="overflow-hidden rounded-[22px] bg-muted/50 shadow-xs"
      style={{ border: "0.5px solid var(--hairline)" }}
    >
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 bg-background px-4 py-3.5">
          <div className="h-9 w-9 shrink-0 rounded-full bg-muted animate-pulse" />
          <div className="h-3 w-40 rounded bg-muted animate-pulse" />
          <div className="h-3 w-1/3 rounded bg-muted animate-pulse ml-4" />
          <div className="h-3 w-24 rounded bg-muted animate-pulse ml-auto" />
        </div>
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div
      data-testid="activity-empty"
      className="rounded-[22px] bg-background p-12 shadow-xs text-center"
      style={{ border: "0.5px solid var(--hairline)" }}
    >
      <div className="mx-auto flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10 mb-4">
        <ListMagnifyingGlass size={28} className="text-primary" />
      </div>
      <h2 className="text-[15px] font-semibold text-foreground">
        No events match these filters.
      </h2>
      <p className="text-[13px] text-muted-foreground mt-2 max-w-md mx-auto">
        Try widening the date range or clearing a filter to see more activity.
      </p>
    </div>
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div
      data-testid="activity-error"
      className="rounded-[22px] bg-background p-12 shadow-xs text-center"
      style={{ border: "0.5px solid var(--hairline)" }}
    >
      <div className="mx-auto flex items-center justify-center w-14 h-14 rounded-2xl bg-destructive/10 mb-4">
        <WarningCircle size={28} className="text-destructive" />
      </div>
      <h2 className="text-[15px] font-semibold text-foreground">
        Couldn't load activity
      </h2>
      <p className="text-[13px] text-muted-foreground mt-2 max-w-md mx-auto">{message}</p>
      <Button
        variant="outline"
        onClick={onRetry}
        className="mt-4 rounded-2xl text-[13px] shadow-xs cursor-pointer hover:bg-background hover:-translate-y-0.5 hover:shadow-sm transition-all"
        style={{ border: "0.5px solid var(--hairline)" }}
      >
        <ArrowClockwise size={16} className="mr-1" />
        Retry
      </Button>
    </div>
  );
}
